import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PanelService } from 'src/app/modules/shared/services/api/panel.service';
import { Router } from '@angular/router';
import { SnackBarService } from 'src/app/modules/shared/services/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { PaymentDialogComponent } from '../payment-dialog/payment-dialog.component';
import {
  Gateway,
  GATEWAY_TYPE,
  GatewayDetail,
  GatewayGroup,
  PaymentConfig
} from 'src/app/modules/shared/models/gateway.model';
import { v4 as uuidv4 } from 'uuid';
import { WalletBalanceService } from 'src/app/modules/shared/services/resolver/wallet-balance.service';
import { StoreDetailService } from 'src/app/modules/shared/services/resolver/store-detail.service';
import { GoogleTagManagerService } from 'src/app/modules/shared/services/google-tag-manager.service';
import { isPlatformBrowser } from '@angular/common';
import { DataLayerService } from 'src/app/modules/shared/services/data-layer/data-layer.service';
import { GoogleEventTypes } from 'src/app/modules/shared/enum/datalayer-event-types';
import { PaymentService } from 'src/app/modules/shared/services/payment.service';
import { BinanceService } from 'src/app/modules/shared/services/binance/binance.service';
import { ProductPricePipe } from 'src/app/modules/shared/pipes/convert-price.pipe';
import { PaymentData } from 'src/app/modules/shared/models/payment.model';
import { LocalStorageService } from 'src/app/modules/shared/services/storage/local-storage.service';
import { CreateOrderRequest } from 'src/app/modules/shared/models/orders.model';
import { FACEBOOK_EVENT_TYPES } from 'src/app/modules/shared/services/meta-pixel/meta-pixel.model';
import { MetaPixelService } from 'src/app/modules/shared/services/meta-pixel/meta-pixel.service';
import { ProfileResolverService } from 'src/app/modules/shared/services/resolver/profile-resolver.service';
import { Profile } from 'src/app/modules/shared/models/profile.mode';
import { GateIoService } from 'src/app/modules/shared/services/gate-io/gate-io.service';
import { MatSelectChange } from '@angular/material/select';
import { KingClubAssets } from 'src/app/modules/shared/enum/icons';

import { Url_Friendly_String } from 'src/app/modules/shared/helpers/helper';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss']
})
export class PaymentComponent implements OnInit, OnDestroy {
  private paymentDataSubscription: Subscription;
  paymentData: PaymentData = undefined;
  gateway: FormControl = new FormControl('', [Validators.required]);
  wallet: FormControl = new FormControl('', [Validators.required]);
  deliveryMethod: string;
  gatewayGroups: GatewayGroup[] = [];
  processingFee: number;
  remaining: any;
  storeDetail;
  isInsufficient: boolean = true;
  isBrowser: boolean;
  isLoggedIn: boolean;
  profileData: Profile;

  kingAssets = KingClubAssets;
  selectedPaymentMethod: FormControl = new FormControl('', [
    Validators.required
  ]);

  currencies = [];
  networks: any[] = [];

  constructor(
    private panelService: PanelService,
    private router: Router,
    private snackBarService: SnackBarService,
    public dialog: MatDialog,
    private binanceService: BinanceService,
    private walletBalance: WalletBalanceService,
    private localStorageService: LocalStorageService,
    private storeDetailResolver: StoreDetailService,
    private googleTagManagerService: GoogleTagManagerService,
    private dataLayerService: DataLayerService,
    private paymentService: PaymentService,
    private metaPixelService: MetaPixelService,
    private convertPricePipe: ProductPricePipe,
    private profileResolver: ProfileResolverService,
    private gateioService: GateIoService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.isLoggedIn = this.localStorageService.isLoggedIn;
    this.getPaymentData();
  }

  private getUrlFriendlyName(name: string) {
    return Url_Friendly_String(name);
  }

  private getPaymentData() {
    this.paymentData = this.paymentService.paymentData;
    if (!this.paymentData) return;

    this.storeDetail = this.storeDetailResolver.permissionInit;
    // this.getGatewayList();
    this.getGatewayGroups();
    this.getWalletBalance();
    this.checkBalanceRemaining();
    this.getProfileData();
    this.calcPayablePrice();
  }

  private getProfileData() {
    this.profileResolver.permissionInitInfo.subscribe((data) => {
      this.profileData = data;
      this.setDelivery();
    });
  }

  private setDelivery() {
    this.deliveryMethod =
      this.paymentData.deliveryInfo.email || this.profileData?.email;
  }

  private calcPayablePrice() {
    const share = parseFloat(this.gateway.value?.share) || 0;
    const productPrice = this.paymentService.getPayableAmount({
      discount: +this.paymentData.product.discount,
      itemPrice: +this.paymentData.order.basePrice,
      itemCount: this.paymentData.order.orderCount,
      exchangeRate: this.paymentData.order.exchangeRate,
      activationFee: this.paymentData.order.activationFee,
      share
    });
    this.paymentData.order.payablePriceWithoutDiscount =
      productPrice.payablePriceWithoutDiscount;
    this.paymentData.order.payablePrice = productPrice.payablePrice;
    this.setSufficient();
    this.setProcessingFee(share);
  }

  private getWalletBalance() {
    this.walletBalance.permissionInitInfo.subscribe((data) => {
      this.remaining = data;
    });
  }

  private setProcessingFee(share: number = 0) {
    this.processingFee = this.paymentService.getProcessingFee({
      activationFee: this.paymentData.order.activationFee,
      discount: this.paymentData.product.discount,
      exchangeRate: this.paymentData.order.exchangeRate,
      itemCount: this.paymentData.order.orderCount,
      itemPrice: this.paymentData.order.basePrice,
      share
    });
  }

  private checkBalanceRemaining() {
    const walletSubscription = this.wallet.valueChanges.subscribe((value) => {
      this.setSufficient();
    });
  }

  setSufficient() {
    this.isInsufficient =
      this.convertPricePipe.transform(this.paymentData.order.payablePrice) >
      this.convertPricePipe.transform(+this.remaining);
  }

  private getGatewayGroups() {
    this.panelService.getGatewayGroups().subscribe(
      (gateways) => {
        this.gatewayGroups = [...gateways];
      },
      (error) => {
        this.gatewayGroups = [];
      }
    );
  }

  onGatewayChange(gw: GatewayDetail) {
    this.gateway.setValue(gw);
    this.resetWallet();
    this.calcPayablePrice();
  }

  private sendPurchaseEvent(data: any) {
    this.dataLayerService.pushGoogleEvent(GoogleEventTypes.PURCHASE, data);
    this.metaPixelService.pushEvent(FACEBOOK_EVENT_TYPES.PURCHASE, {
      data: {
        content_ids: [`${this.paymentData.product.id}`],
        contents: [
          {
            id: `${this.paymentData.product.id}`,
            quantity: this.paymentData.order.orderCount
          }
        ],
        content_type: 'product',
        num_items: 1,
        currency: this.paymentData.currency.code,
        value: this.paymentData.order.payablePrice
      }
    });
  }

  private hiPinPayment(order) {
    const dialogRef = this.dialog.open(PaymentDialogComponent, {
      minWidth: '300px',
      maxWidth: '500px',
      panelClass: 'full-with-dialog',
      data: { order, title: order.gateway_detail.title }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        let chargeWalletPurchaseData = {
          amount: this.paymentData.order.payablePrice,
          wallet_transaction_id: order?.wallet_transaction_id,
          currency: order?.gateway_detail?.currency_detail?.symbol,
          gateway: order?.gateway_detail.title,
          gateway_id: order?.gateway_detail?.id,
          gateway_type: order?.gateway_detail?.gateway_type,
          product: `${order?.product_detail.id}_${order?.product_detail.name}`
        };

        this.googleTagManagerService.sendAnalytics(
          'charge_wallet_with_purchase',
          { ...chargeWalletPurchaseData }
        );
        this.sendPurchaseEvent({
          currency: order.gateway_detail.currency_detail.code,
          transactionId: order.wallet_transaction_id,
          payablePrice: this.paymentData.order.payablePrice,
          id: order.product_detail.id,
          name: order.product_detail.name,
          discount: this.paymentData.product.discount,
          price: order.item_price,
          quantity: order.item_count,
          orderAmount: order.order_amount
        });
        this.router.navigate(['/payment'], {
          queryParams: { redeem: order.id }
        });
      }
    });
  }

  handleLogin() {
    if (!this.localStorageService.isLoggedIn) {
      this.router.navigate(['/authenticate/login'], {
        queryParams: {
          access: 'false',
          checkout: true
        }
      });
    }
  }

  itemCountChanged(event: MatSelectChange) {
    this.calcPayablePrice();
  }

  checkWallet() {
    if (this.isInsufficient) {
      this.snackBarService.showSnackBar(
        'Insufficient balance in your wallet. Please top up.',
        'primary',
        2000
      );
    }
  }

  resetGateways() {
    this.gateway.reset();
  }

  resetWallet() {
    this.wallet.reset();
  }

  submitPayment() {
    if (this.gateway.invalid && this.wallet.invalid) {
      return;
    }

    this.handlePaymentAnalytics();

    let gateway = this.gateway.value;
    if (gateway) {
      if (gateway.gateway_type == GATEWAY_TYPE.PAYPAL) {
        this.googleTagManagerService.sendAnalytics('purchase_gateway', {
          gateway: 'Paypal'
        });
      } else {
        const order: CreateOrderRequest = {
          item_price: this.paymentData.order.basePrice,
          one_item_price: this.paymentData.order.exchangedPrice,
          item_count: this.paymentData.order.orderCount,
          callback_url: this.paymentData.callbackUrl,
          gateway: `${this.gateway.value?.id}`,
          email: this.deliveryMethod,
          sku: this.paymentData.order.sku,
          send_to_friend: !!this.paymentData.deliveryInfo.giftInfo,
          ...(!!this.paymentData.deliveryInfo.giftInfo && {
            friend_name: this.paymentData.deliveryInfo.giftInfo.name,
            description: this.paymentData.deliveryInfo.giftInfo.note
          })
        };
        this.panelService.createOrder(order).subscribe((order) => {
          if (order?.status == -1) {
            this.snackBarService.showSnackBar(
              order?.error_display,
              'primary',
              2000
            );
            return;
          }
          if (
            order?.gateway_detail?.gateway_type ==
              GATEWAY_TYPE.PROVIDER_HOSTED_PAGE ||
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.BINANCE_PAY
          ) {
            let extra_data = order?.payment_detail?.extra_info;
            let payload = {
              certSn: extra_data['CertSn'],
              merchantId: `${extra_data['MerchantId']}`,
              timeStamp: extra_data['TimeStamp'],
              nonceStr: extra_data['NonceStr'],
              package: `prepay_id=${extra_data['PrepayId']}`,
              paySign: extra_data['PaySign']
            };
            if (this.binanceService.isBinance) {
              this.binanceService.requestBinancePayment(
                payload,
                `/payment?order_id=${order.id}`
              );
              return;
            } else {
              window.location.href = order?.payment_detail?.redirect_url;
            }
          }
          if (
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.MARAL ||
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.BITPAY ||
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.NGENIUS ||
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.EZPINPAYMENT ||
            order?.gateway_detail?.gateway_type ==
              GATEWAY_TYPE.CRYPTO_GATEWAY ||
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.GATEIO ||
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.KUCOIN
          ) {
            if (order?.payment_detail) {
              this.googleTagManagerService.sendAnalytics('purchase_gateway', {
                gateway: order?.gateway_detail?.title
              });
              if (this.isBrowser) {
                window.location.href = order?.payment_detail?.redirect_url;
              }
            }
          } else if (order?.gateway_detail?.gateway_type == 1) {
            if (order?.payment_detail) {
              this.googleTagManagerService.sendAnalytics('purchase_gateway', {
                gateway: order?.gateway_detail?.title
              });
              this.hiPinPayment(order);
            }
          } else if (
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.GATEIO
          ) {
            const refrenceCode = order?.payment_detail?.reference_code;
            if (refrenceCode) {
              this.router.navigateByUrl('/crypto/gateway/' + refrenceCode);
            }
          } else if (
            order?.gateway_detail?.gateway_type == GATEWAY_TYPE.GATELIFE
          ) {
            this.gateioService.handlePayment(order?.payment_detail?.extra_info);
          }
        });
      }
    } else if (this.wallet.value) {
      const reference_number = uuidv4();
      let order: CreateOrderRequest = {
        reference_number,
        one_item_price: this.paymentData.order.exchangedPrice,
        item_count: this.paymentData.order.orderCount,
        sku: this.paymentData.order.sku,
        item_price: this.paymentData.order.basePrice,
        callback_url: this.paymentData.callbackUrl,
        email: this.deliveryMethod,
        purchase_type: 1,
        send_to_friend: !!this.paymentData.deliveryInfo.giftInfo,
        ...(!!this.paymentData.deliveryInfo.giftInfo && {
          friend_name: this.paymentData.deliveryInfo.giftInfo.name,
          description: this.paymentData.deliveryInfo.giftInfo.note
        })
      };
      this.panelService.createOrder(order).subscribe((order) => {
        if (order?.status == -1) {
          this.snackBarService.showSnackBar(
            order?.error_display,
            'primary',
            2000
          );
          return;
        } else {
          this.sendPurchaseEvent({
            currency: order.currency_detail.code,
            transactionId: order.wallet_transaction_id,
            payablePrice: this.paymentData.order.payablePrice,
            id: order.product_detail?.id,
            name: order.product_detail?.name,
            discount: this.paymentData.product.discount,
            price: order.item_price,
            quantity: order.item_count,
            orderAmount: order.order_amount
          });
          this.router.navigate(['/payment'], {
            queryParams: { wallet_payment: order.id }
          });
          this.walletBalance.updateInfo();
        }
      });
    }
  }

  private handlePaymentAnalytics() {
    this.googleTagManagerService.clearPreviousEcommerce();
    let paymentInfo = {
      transaction_id: this.paymentData.product?.id,
      value: this.paymentData.order?.exchangedPrice.toFixed(2),
      currency: this.paymentData.currency.symbol,
      items: [
        {
          item_id: this.paymentData.product?.id,
          item_name: this.paymentData.product?.name,
          discount: this.paymentData.product.discount,
          price: this.paymentData.order.basePrice,
          quantity: this.paymentData.order.orderCount
        }
      ]
    };
    if (this.paymentData.product?.categories) {
      this.paymentData.product?.categories.forEach((category, index) => {
        if (index == 0) {
          paymentInfo.items[`item_category`] = category.name;
        } else {
          paymentInfo.items[`item_category${index}`] = category.name;
        }
      });
    }

    const eventData = {
      ...this.paymentData.product,
      currency: this.paymentData.currency.code,
      payablePrice: this.paymentData.order.payablePrice,
      price: this.paymentData.order.exchangedPrice,
      quantity: this.paymentData.order.orderCount
    };

    this.dataLayerService.pushGoogleEvent(
      GoogleEventTypes.ADD_SHIPPING_INFO,
      eventData
    );

    this.dataLayerService.pushGoogleEvent(GoogleEventTypes.ADD_PAYMENT_INFO, {
      ...eventData,
      paymentType: this.wallet.value ? 'Wallet' : this.gateway.value?.title
    });

    this.metaPixelService.pushEvent(FACEBOOK_EVENT_TYPES.ADD_PAYMENT_INFO, {
      data: {
        content_ids: [`${this.paymentData.product.id}`],
        contents: [
          {
            id: `${this.paymentData.product.id}`,
            quantity: this.paymentData.order.orderCount
          }
        ],
        currency: this.paymentData.currency.code,
        value: this.paymentData.order.payablePrice
      }
    });
  }

  backButton() {
    this.router.navigate([
      '/product',
      this.paymentData.product.id,
      this.getUrlFriendlyName(this.paymentData.product.name)
    ]);
  }

  walletSelected(paymentMethod: PaymentConfig) {
    this.wallet.setValue('wallet');
    this.checkWallet();
    this.calcPayablePrice();
  }

  ngOnDestroy(): void {
    this.paymentDataSubscription?.unsubscribe();
  }
}
